import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { OrganizationPaymentMethod, RiderFare, PaymentCollectionConstants } from '@uzurv/clara';
import { CamelCasePipe, isNullOrUndefined, PaymentMethodConstants } from '@uzurv/core';
import { SubmitRiderFare } from '../../../store/trip-detail/trip-detail.actions';
import { TripDetailState } from '../../../store/trip-detail/trip-detail.reducer';
import { isRiderFareCollected, isSubmittingRiderFares } from '../../../store/trip-detail/trip-detail.selectors';
import { takeUntil } from 'rxjs/operators';
import { FareCollectionValidators } from '../../../validators';
import { BaseComponent } from '../../base/base.component';
import { ReservationListItemVm } from '../../reservation';

@Component({
  selector: 'app-rider-fare-collection-dialog',
  templateUrl: './rider-fare-collection-dialog.component.html',
  styleUrls: ['./rider-fare-collection-dialog.component.scss']
})
export class RiderFareCollectionDialogComponent extends BaseComponent implements OnInit, OnDestroy {
  readonly paymentMethodConstants = PaymentMethodConstants;

  isSubmittingRiderFares$ = this.store$.select(isSubmittingRiderFares);

  isCashCollectionEnabled = false;

  // Supported payment methods.
  supportedPaymentMethods = this.data?.supportedPaymentMethods
    .filter(
      organizationPaymentMethod =>
        organizationPaymentMethod?.paymentMethod?.paymentCollectionType?.code === PaymentCollectionConstants.FARE_COLLECTION
    )
    .sort((a, b) => {
      // Force cash to the top
      if (b.paymentMethod.name === PaymentMethodConstants.CASH) {
        return -1;
      }
      return a.name.localeCompare(b.name);
    });

  // Amount to be collected from the rider.
  amountDue = this.data?.rideRequestDetail.requestFee;

  // Amount of billable passengers for the ride.
  billablePassengers = this.data?.rideRequestDetail.billablePassengerCount;

  // Controls the message under 'Enter Amount' if status was changed before payment was collected.
  riderFareCollected$ = this.store$.select(isRiderFareCollected);

  // Keeps track of whether the modal was opened from an update status click.
  fromStatus = this.data?.fromStatus;

  // Keeps track of the current dollar amount of combined fields. Initialized to 0 for view display.
  runningTotal: number = 0;

  // Boolean for whether or not runningTotal exceeds amountDue.
  overcharged = false;

  riderCannotPayFare: boolean = false;

  // New form group for the dialog, contains controls for fields.
  fareCollectionForm = this.fb.group({
    cannotPayFare: this.fb.control(false)
  });

  constructor(
    private dialogRef: MatDialogRef<RiderFareCollectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReservationListItemVm,
    private fb: UntypedFormBuilder,
    private camelCasePipe: CamelCasePipe,
    private store$: Store<State<TripDetailState>>
  ) {
    super();
  }

  /*
   * Iterate over the supported payment methods array being passed in,
   * If the method is cash, create an entry specific to cash.
   * If the method is not cash, create a more generic entry based on having a ticket/pass number and value.
   */
  ngOnInit(): void {
    this.supportedPaymentMethods.forEach(method => {
      const methodName = this.getFormControlName(method);

      if (method.paymentMethod.code === PaymentMethodConstants.CASH) {
        this.fareCollectionForm.addControl(methodName, this.fb.control(0));

        // Watch for value changes to the cash field and trigger a calculate running total when changed for real time tracking.
        this.fareCollectionForm
          .get(methodName)
          .valueChanges.pipe(takeUntil(this.destroyed$))
          .subscribe(() => {
            this.calculateRunningTotal();
          });
      } else {
        this.fareCollectionForm.addControl(methodName, this.fb.array([]));
      }
    });

    /*
     * Watch for value changes for the cannot pay fare checkbox.
     * If the checkbox is checked, disable all other fields. If it is not checked, enable all other fields.
     */
    this.fareCollectionForm
      .get('cannotPayFare')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.supportedPaymentMethods.forEach(method => {
          const methodName = this.getFormControlName(method);
          if (method.ticketNumberRequired === false) {
            if (this.fareCollectionForm.get('cannotPayFare').value === true) {
              this.fareCollectionForm.get(methodName).reset();
              this.fareCollectionForm.get(methodName).disable();
            } else {
              this.fareCollectionForm.get(methodName).enable();
            }
          } else {
            for (const value of this.getPaymentMethodAsArray(methodName).controls) {
              if (this.fareCollectionForm.get('cannotPayFare').value === true) {
                value.reset();
                value.disable();
              } else {
                value.enable();
              }
            }
          }
        });
      });
  }

  /*
   * Gets the camel cased version of the payment method name for use in html.
   */
  getFormControlName(organizationPaymentMethod: OrganizationPaymentMethod): string {
    return this.camelCasePipe.transform(organizationPaymentMethod.name);
  }

  /*
   * Returns the payment method array as a FormArray.
   */
  getPaymentMethodAsArray(methodName: string): UntypedFormArray {
    return this.fareCollectionForm.get(methodName) as UntypedFormArray;
  }

  /*
   * Returns a new entry for any payment method array.
   */
  newPaymentEntry(organizationPaymentMethod: OrganizationPaymentMethod): UntypedFormGroup {
    if (organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.CONFIRMATION) {
      const formGroup = this.fb.group({
        confirmed: this.fb.control(false, FareCollectionValidators.voucherValueValidator(organizationPaymentMethod))
      });

      formGroup
        .get('confirmed')
        .valueChanges.pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.calculateRunningTotal();
        });

      return formGroup;
    } else {
      let initialValue = 0;
      let disabled = false;

      if (
        organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.PAWPASS ||
        organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.RIDEPASS ||
        organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.MULTIRIDEPASS
      ) {
        initialValue = parseFloat((this.amountDue / this.billablePassengers).toFixed(2));
        disabled = true;
      }

      const formGroup = this.fb.group({
        paymentNumber: this.fb.control(null, [
          FareCollectionValidators.voucherNumberValidator(organizationPaymentMethod),
          this.maxVoucherNumberUsageValidator(organizationPaymentMethod)
        ]),
        paymentValue: this.fb.control(
          { value: initialValue, disabled: disabled },
          Validators.compose([Validators.min(0.01), FareCollectionValidators.voucherValueValidator(organizationPaymentMethod)])
        )
      });

      formGroup
        .get('paymentValue')
        .valueChanges.pipe(takeUntil(this.destroyed$))
        .subscribe(value => {
          this.calculateRunningTotal();

          const paymentNumber = formGroup.get('paymentNumber');
          if (!isNullOrUndefined(paymentNumber)) {
            paymentNumber.markAsTouched();
          }
        });

      formGroup
        .get('paymentNumber')
        .valueChanges.pipe(takeUntil(this.destroyed$))
        .subscribe(value => {
          this.calculateRunningTotal();

          const paymentValue = formGroup.get('paymentValue');
          if (!isNullOrUndefined(paymentValue)) {
            paymentValue.markAsTouched();
          }
        });

      return formGroup;
    }
  }

  /*
   * Checks to see if the voucher number is invalid in order to display an error message.
   */
  invalidVoucherNumber(organizationPaymentMethod: OrganizationPaymentMethod, index: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (!isNullOrUndefined(methodArray['controls'][index].get('paymentNumber').errors)) {
      if (methodArray['controls'][index].get('paymentNumber').errors['invalidVoucherNumber']) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /*
   * Checks to see if the voucher number contains special characters in order to display an error message.
   */
  noSpecialCharacters(organizationPaymentMethod: OrganizationPaymentMethod, index: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (!isNullOrUndefined(methodArray['controls'][index].get('paymentNumber').errors)) {
      if (methodArray['controls'][index].get('paymentNumber').errors['noSpecialCharacters']) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /*
   * Checks to see if the voucher number is a duplicate.
   */
  maxVoucherNumberUsageValidator(organizationPaymentMethod: OrganizationPaymentMethod): ValidatorFn {
    return (abstractControl: AbstractControl): ValidationErrors | null => {
      const validationErrors: { [key: string]: boolean } = {};
      const methodName = this.getFormControlName(organizationPaymentMethod);
      const methodArray = this.getPaymentMethodAsArray(methodName);
      const numberOfUses = organizationPaymentMethod.validNumberOfUses;
      let count = 0;

      if (!isNullOrUndefined(abstractControl.value) && abstractControl.value !== '') {
        if (methodArray['controls'].length > 1) {
          for (const control of methodArray['controls']) {
            if (abstractControl.value.toLowerCase() === control.get('paymentNumber').value.toLowerCase()) {
              count += 1;
            }
          }
        }

        if (!isNullOrUndefined(numberOfUses)) {
          if (count > numberOfUses) {
            validationErrors['maxUsageExceeded'] = true;
          }
        }
      }

      return validationErrors;
    };
  }

  /*
   * Checks to see if the voucher number is a duplicate in order to display an error message.
   */
  maxVoucherNumberUsage(organizationPaymentMethod: OrganizationPaymentMethod, index: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (!isNullOrUndefined(methodArray['controls'][index].get('paymentNumber').errors)) {
      if (methodArray['controls'][index].get('paymentNumber').errors['maxUsageExceeded']) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /*
   * Checks to see if the voucher number is a duplicate in order to display an error message.
   */
  requiredVoucherNumber(organizationPaymentMethod: OrganizationPaymentMethod, index: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (!isNullOrUndefined(methodArray['controls'][index].get('paymentNumber').errors)) {
      if (methodArray['controls'][index].get('paymentNumber').errors['required']) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /*
   * Checks to see if the voucher number is a duplicate in order to display an error message.
   */
  requiredVoucherValue(organizationPaymentMethod: OrganizationPaymentMethod, index: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (organizationPaymentMethod.ticketNumberRequired === true) {
      if (!isNullOrUndefined(methodArray['controls'][index].get('paymentValue').errors)) {
        if (methodArray['controls'][index].get('paymentValue').errors['min']) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  }

  /*
   * Checks to see if the voucher number is invalid in order to display an error message.
   */
  invalidVoucherValue(organizationPaymentMethod: OrganizationPaymentMethod, index?: number): boolean {
    const methodName = this.getFormControlName(organizationPaymentMethod);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (organizationPaymentMethod.ticketNumberRequired === true) {
      if (!isNullOrUndefined(methodArray['controls'][index].get('paymentValue').errors)) {
        if (methodArray['controls'][index].get('paymentValue').errors['invalidVoucherValue']) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      if (!isNullOrUndefined(methodArray.errors)) {
        if (methodArray.errors['invalidVoucherValue']) {
          return true;
        } else {
          return false;
        }
      }
    }
  }

  /*
   * Gets the text for the add button
   */
  getAddButtonText(organizationPaymentMethod: OrganizationPaymentMethod): string {
    if (isNullOrUndefined(organizationPaymentMethod)) {
      return null;
    }

    if (organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.CASH) {
      return 'Enter Amount';
    }

    if (!isNullOrUndefined(organizationPaymentMethod.metadata) && !isNullOrUndefined(organizationPaymentMethod.metadata['addButtonText'])) {
      return organizationPaymentMethod.metadata['addButtonText'];
    }

    if (
      organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.RIDEPASS ||
      organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.MULTIRIDEPASS ||
      organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.PAWPASS
    ) {
      return 'Add Pass';
    }

    return 'Add ' + organizationPaymentMethod.name;
  }

  /*
   * Handles the user clicking the 'add another' button.
   */
  addAnotherClick(organizationPaymentMethod: OrganizationPaymentMethod): void {
    if (this.fareCollectionForm.get('cannotPayFare').value === false) {
      if (organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.CASH) {
        this.isCashCollectionEnabled = true;
      } else {
        const methodName = this.getFormControlName(organizationPaymentMethod);
        const methodArray = this.getPaymentMethodAsArray(methodName);

        methodArray.push(this.newPaymentEntry(organizationPaymentMethod));

        this.calculateRunningTotal();
      }
    }
  }

  /*
   * Checks to see if the 'add another' button should be displayed to the user for a given pass.
   */
  shouldShowAddAnotherButton(method: any, index: number): boolean {
    const methodName = this.getFormControlName(method);
    const methodArray = this.getPaymentMethodAsArray(methodName);

    if (this.runningTotal >= this.amountDue) {
      return false;
    }

    if (method.paymentMethod.code === 'CONF') {
      const checkboxField = this.fareCollectionForm.get(methodName)['controls'][index].get('confirmed');

      // If both the voucher number and amount have a value and are valid and is the LAST entry in the array, display the button.
      // Only shows for last entry in the array to prevent it from showing up below earlier entries.
      if (checkboxField.value === true && methodArray.length - 1 === index) {
        return true;
      } else {
        return false;
      }
    } else if (
      method.paymentMethod.code === PaymentMethodConstants.PAWPASS ||
      method.paymentMethod.code === PaymentMethodConstants.RIDEPASS ||
      method.paymentMethod.code === PaymentMethodConstants.MULTIRIDEPASS
    ) {
      const paymentNumberField = this.fareCollectionForm.get(methodName)['controls'][index].get('paymentNumber');
      const paymentValueField = this.fareCollectionForm.get(methodName)['controls'][index].get('paymentValue');

      if (
        (paymentNumberField.dirty || !method.ticketNumberRequired) &&
        paymentNumberField.valid &&
        paymentNumberField.value !== '' &&
        paymentValueField.value !== 0 &&
        methodArray.length - 1 === index
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      const paymentNumberField = this.fareCollectionForm.get(methodName)['controls'][index].get('paymentNumber');
      const paymentValueField = this.fareCollectionForm.get(methodName)['controls'][index].get('paymentValue');

      // If both the voucher number and amount have a value and are valid and is the LAST entry in the array, display the button.
      // Only shows for last entry in the array to prevent it from showing up below earlier entries.
      if (
        (paymentNumberField.dirty || !method.ticketNumberRequired) &&
        paymentNumberField.valid &&
        paymentNumberField.value !== '' &&
        paymentValueField.dirty &&
        paymentValueField.valid &&
        paymentValueField.value !== 0 &&
        methodArray.length - 1 === index
      ) {
        return true;
      } else {
        return false;
      }
    }
  }

  /*
   * Handles the user clicking the 'delete' button.
   */
  deleteFareClicked(organizationPaymentMethod: OrganizationPaymentMethod, index: number): void {
    if (this.fareCollectionForm.get('cannotPayFare').value === false) {
      const methodName = this.getFormControlName(organizationPaymentMethod);

      if (organizationPaymentMethod.paymentMethod.code === PaymentMethodConstants.CASH) {
        this.isCashCollectionEnabled = false;
        this.fareCollectionForm.get(methodName).reset();
      } else {
        const methodArray = this.getPaymentMethodAsArray(methodName);
        methodArray.removeAt(index);
        this.calculateRunningTotal();
      }
    }
  }

  areButtonsDisabled(): boolean {
    if (this.fareCollectionForm.get('cannotPayFare').value === true) {
      return true;
    } else {
      return false;
    }
  }

  /*
   * Real time checks against certain conditions to see if submit button should be active.
   */
  isSubmitDisabled(): boolean {
    if (this.fareCollectionForm.get('cannotPayFare').value === true) {
      return false;
    } else if (this.runningTotal === this.amountDue && !this.overcharged) {
      return false;
    } else {
      return true;
    }
  }

  /*
   * Calculates the current total collected based on the cash values entered so far.
   */
  calculateRunningTotal(): void {
    this.runningTotal = 0;

    // Iterate over all payment value fields and add the values to the running total.
    this.supportedPaymentMethods.forEach(method => {
      const methodName = this.getFormControlName(method);
      if (method.paymentMethod.code === PaymentMethodConstants.CASH && !isNullOrUndefined(this.fareCollectionForm.get('cash'))) {
        this.runningTotal += this.fareCollectionForm.get('cash').value;
      } else {
        const formArray = this.getPaymentMethodAsArray(methodName);

        if (!isNullOrUndefined(formArray)) {
          for (const subForm of formArray.controls) {
            if (method.paymentMethod.code === PaymentMethodConstants.CONFIRMATION) {
              if (subForm['controls'].confirmed.value === true) {
                this.runningTotal += this.amountDue / this.billablePassengers;
              }
            } else {
              const paymentValue = subForm['controls'].paymentValue;

              if (paymentValue.valid || paymentValue.disabled) {
                this.runningTotal += paymentValue.value;
              }
            }
          }
        }
      }
    });

    // Ensure the running total is to 2 decimal places.
    this.runningTotal = parseFloat(this.runningTotal.toFixed(2));

    // Check to see if the running total is greater than the amount due.
    if (this.runningTotal > this.amountDue) {
      this.overcharged = true;
    } else {
      this.overcharged = false;
    }
  }

  /*
   * Closes the dialog modal.
   */
  cancel(): void {
    this.dialogRef.close();
  }

  /*
   * Submits the form data.
   */
  submitForm(): void {
    if (!this.fareCollectionForm.valid) {
      this.fareCollectionForm.markAllAsTouched();
      return;
    }

    const reservationId = this.data?.id;
    const riderFareArray: Array<RiderFare> = [];
    const dialogRef = this.dialogRef;
    const reservationListItemVm = this.data;

    if (this.fareCollectionForm.get('cannotPayFare').value) {
      const cannotPayRiderFare: RiderFare = new RiderFare();
      cannotPayRiderFare.reservationId = reservationId;
      cannotPayRiderFare.fareCollected = false;
      cannotPayRiderFare.collectedOffline = false;

      this.store$.dispatch(
        SubmitRiderFare({ payload: { reservationId, riderFareArray: [cannotPayRiderFare], dialogRef, reservationListItemVm } })
      );
    } else {
      this.supportedPaymentMethods.forEach(method => {
        const methodName = this.getFormControlName(method);

        // Create a RiderFare specific to cash payment method.
        if (method.paymentMethod.code === PaymentMethodConstants.CASH) {
          if (this.fareCollectionForm.get(methodName).value > 0) {
            const riderFare = new RiderFare();
            riderFare.organizationPaymentMethodName = method.name;
            riderFare.reservationId = reservationId;
            riderFare.fareCollected = true;
            riderFare.paymentMethodId = method.paymentMethod.id;
            riderFare.cashAmount = this.fareCollectionForm.get(methodName).value;
            riderFare.collectedOffline = false;

            riderFareArray.push(riderFare);
          }

          // Create a RiderFare specific to confirmation payment method.
        } else if (method.paymentMethod.code === PaymentMethodConstants.CONFIRMATION) {
          for (const control of this.fareCollectionForm.get(methodName)['controls']) {
            if (control.get('confirmed').value === true) {
              const confirmationValue = parseFloat((this.amountDue / this.billablePassengers).toFixed(2));

              if (confirmationValue > 0) {
                const riderFare = new RiderFare();
                riderFare.organizationPaymentMethodName = method.name;
                riderFare.reservationId = reservationId;
                riderFare.fareCollected = true;
                riderFare.paymentMethodId = method.paymentMethod.id;
                riderFare.confirmationValue = confirmationValue;
                riderFare.collectedOffline = false;

                riderFareArray.push(riderFare);
              }
            }
          }
          // Create a RiderFare for all other types.
        } else {
          for (const control of this.fareCollectionForm.get(methodName)['controls']) {
            let ticketNumber = null;
            const ticketValue = control.get('paymentValue').value;

            if (ticketValue > 0) {
              if (method.ticketNumberRequired) {
                ticketNumber = control.get('paymentNumber').value;
              }

              const riderFare = new RiderFare();
              riderFare.organizationPaymentMethodName = method.name;
              riderFare.reservationId = reservationId;
              riderFare.fareCollected = true;
              riderFare.paymentMethodId = method.paymentMethod.id;
              riderFare.ticketNumber = ticketNumber;
              riderFare.ticketValue = ticketValue;
              riderFare.collectedOffline = false;

              riderFareArray.push(riderFare);
            }
          }
        }
      });

      // Submit data to endpoint.
      this.store$.dispatch(SubmitRiderFare({ payload: { reservationId, riderFareArray, dialogRef, reservationListItemVm } }));
    }
  }
}
